import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@client/components/hooks/use-toast';
import {
  selectSessionAdvisor,
  selectAdvisorHierarchyInvitationAcceptResponse,
  selectAdvisorHierarchyInvitationDeclineResponse,
  selectPendingSupervisorInvitations,
  selectSession,
} from '@client/model/selectors';
import {
  postAdvisorHierarchyInvitationAcceptByInvitationIdRequest,
  postAdvisorHierarchyInvitationDeclineByInvitationIdRequest,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
} from '@client/model/advisorHierarchyModel';
import { SessionType } from '@shared/SessionType';

interface ConsentData {
  hasConsented: boolean;
  ipAddress: string | null;
  userAgent: string;
}

interface UseAdvisorHierarchyAcceptDeclineOptions {
  targetAdvisorId?: string | null;
  /** Whether admin mode is active - disables accept/decline functionality when true */
  adminModeActive?: boolean;
}

/**
 * Hook for managing accept/decline actions on pending supervisor invitations
 * from the Member view of the Team page.
 *
 * This hook handles:
 * - Fetching pending invitations from the Redux store
 * - Managing accept/decline dialog state
 * - Dispatching accept/decline requests with consent data
 * - Showing success/error toast notifications
 * - Auto-refetching relationships on success
 *
 * @param options.targetAdvisorId - Optional advisor ID for admin mode. If provided, manages that advisor's team.
 *                                  If not provided, uses the session advisor (normal mode).
 * @param options.adminModeActive - When true, accept/decline actions are disabled (visible but not functional).
 */
export function useAdvisorHierarchyAcceptDecline({
  targetAdvisorId,
  adminModeActive = false,
}: UseAdvisorHierarchyAcceptDeclineOptions = {}) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { sessionType } = useSelector(selectSession);
  const pendingInvitations = useSelector(selectPendingSupervisorInvitations);
  const acceptResponse = useSelector(selectAdvisorHierarchyInvitationAcceptResponse);
  const declineResponse = useSelector(selectAdvisorHierarchyInvitationDeclineResponse);

  // Dialog state for accept (with consent)
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [invitationToAccept, setInvitationToAccept] = useState<PendingSupervisorInvitation | null>(
    null,
  );

  // Dialog state for decline confirmation
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [invitationToDecline, setInvitationToDecline] =
    useState<PendingSupervisorInvitation | null>(null);

  // Track previous mutation states for completion detection
  const prevAcceptingRef = useRef(false);
  const prevDecliningRef = useRef(false);

  const isAccepting = acceptResponse.status === 'loading';
  const isDeclining = declineResponse.status === 'loading';
  const isProcessing = isAccepting || isDeclining;

  /**
   * Handles post-mutation navigation based on session type.
   * New advisors in signup flow are redirected to action required view.
   * For existing advisors, the middleware will handle refetching relationships.
   */
  const handlePostMutationAction = useCallback(() => {
    if (
      sessionType === SessionType.advisorUnverifiedAccount ||
      sessionType === SessionType.advisorNoSubscription
    ) {
      if (sessionAdvisor?.advisorId) {
        navigate(`/advisors/${sessionAdvisor.advisorId}/account-action-required`);
      }
    }
    // For other session types, the invalidateAdvisorHierarchyMiddleware will handle refetching
  }, [sessionType, sessionAdvisor?.advisorId, navigate]);

  // Handle accept mutation completion
  useEffect(() => {
    if (prevAcceptingRef.current && !isAccepting) {
      if (acceptResponse.status === 'error') {
        toast({
          title: 'Error',
          description: acceptResponse.error || 'Failed to accept invitation. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (acceptResponse.status === 'success') {
        toast({
          title: 'Invitation Accepted',
          description: acceptResponse.message || 'You have joined the team successfully.',
          className: '-translate-y-5',
        });

        setAcceptDialogOpen(false);
        setInvitationToAccept(null);
        handlePostMutationAction();
      }
    }

    prevAcceptingRef.current = isAccepting;
  }, [
    isAccepting,
    acceptResponse.status,
    acceptResponse.error,
    acceptResponse.message,
    handlePostMutationAction,
    toast,
  ]);

  // Handle decline mutation completion
  useEffect(() => {
    if (prevDecliningRef.current && !isDeclining) {
      if (declineResponse.status === 'error') {
        toast({
          title: 'Error',
          description: declineResponse.error || 'Failed to decline invitation. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (declineResponse.status === 'success') {
        toast({
          title: 'Invitation Declined',
          description: declineResponse.message || 'The invitation has been declined.',
          className: '-translate-y-5',
        });

        setDeclineDialogOpen(false);
        setInvitationToDecline(null);
        handlePostMutationAction();
      }
    }

    prevDecliningRef.current = isDeclining;
  }, [
    isDeclining,
    declineResponse.status,
    declineResponse.error,
    declineResponse.message,
    handlePostMutationAction,
    toast,
  ]);

  /**
   * Opens the accept dialog for a specific invitation
   */
  const handleAccept = useCallback((invitation: PendingSupervisorInvitation) => {
    setInvitationToAccept(invitation);
    setAcceptDialogOpen(true);
  }, []);

  /**
   * Confirms acceptance with consent data
   */
  const confirmAccept = useCallback(
    (consentData: ConsentData) => {
      // Block action in admin mode
      if (adminModeActive) {
        toast({
          title: 'Action Not Allowed',
          description: 'Cannot accept invitations on behalf of another advisor.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
        setAcceptDialogOpen(false);
        setInvitationToAccept(null);
        return;
      }

      if (!invitationToAccept) return;

      dispatch(
        postAdvisorHierarchyInvitationAcceptByInvitationIdRequest({
          invitationId: invitationToAccept.invitationId,
          ipAddress: consentData.ipAddress,
          userAgent: consentData.userAgent,
          metadata: { consentCapturedAt: new Date().toISOString() },
        }),
      );
    },
    [dispatch, invitationToAccept, adminModeActive, toast],
  );

  /**
   * Cancels the accept dialog
   */
  const cancelAccept = useCallback(() => {
    setAcceptDialogOpen(false);
    setInvitationToAccept(null);
  }, []);

  /**
   * Opens the decline confirmation dialog for a specific invitation
   */
  const handleDecline = useCallback((invitation: PendingSupervisorInvitation) => {
    setInvitationToDecline(invitation);
    setDeclineDialogOpen(true);
  }, []);

  /**
   * Confirms decline - keeps dialog open while processing
   */
  const confirmDecline = useCallback(() => {
    // Block action in admin mode
    if (adminModeActive) {
      toast({
        title: 'Action Not Allowed',
        description: 'Cannot decline invitations on behalf of another advisor.',
        variant: 'destructive',
        className: '-translate-y-5',
      });
      setDeclineDialogOpen(false);
      setInvitationToDecline(null);
      return;
    }

    if (!invitationToDecline) return;

    dispatch(
      postAdvisorHierarchyInvitationDeclineByInvitationIdRequest({
        invitationId: invitationToDecline.invitationId,
      }),
    );
    // Dialog will be closed by the useEffect when decline completes
  }, [dispatch, invitationToDecline, adminModeActive, toast]);

  /**
   * Cancels the decline dialog
   */
  const cancelDecline = useCallback(() => {
    setDeclineDialogOpen(false);
    setInvitationToDecline(null);
  }, []);

  return {
    // Data
    pendingInvitations,

    // Accept dialog state
    acceptDialogOpen,
    invitationToAccept,

    // Decline dialog state
    declineDialogOpen,
    invitationToDecline,

    // Mutation state
    isAccepting,
    isDeclining,
    isProcessing,

    // Admin mode state
    isDisabled: adminModeActive,

    // Actions
    handleAccept,
    confirmAccept,
    cancelAccept,
    handleDecline,
    confirmDecline,
    cancelDecline,
  };
}

export type UseAdvisorHierarchyAcceptDeclineReturn = ReturnType<
  typeof useAdvisorHierarchyAcceptDecline
>;
